'use client'
import { useRef, useEffect, useState } from "react";
import { createClient } from "@/prismicio";
import { PrismicRichText, PrismicLink } from "@prismicio/react";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import { isFilled } from "@prismicio/client";

/**
 * @typedef {import("@prismicio/client").Content.GuideListSlice} GuideListSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<GuideListSlice>} GuideListProps
 * @type {import("react").FC<GuideListProps>}
 */

// Composant pour un guide
const GuideCard = ({ guide, featured }) => {
  const imageUrl = guide.data.preview_image?.url;
  const imageAlt = guide.data.preview_image?.alt || guide.data.preview_title?.[0]?.text || "Image du guide";
  const date = isFilled.date(guide.data.preview_date)
    ? new Date(guide.data.preview_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' })
    : null;
  const tag = guide.tags?.[0];

  return (
    <article
      className={`${featured ? 'w-full' : 'md:w-full'} h-full flex flex-col md:flex-row`}
      itemScope
      itemType="https://schema.org/BlogPosting"
    >
      <PrismicLink
        document={guide}
        className={`flex flex-col justify-between no-underline relative bg-cover bg-center bg-no-repeat  p-5 md:p-7 text-white min-h-70 md:min-h-80`}
        style={{ backgroundImage: `linear-gradient(#003A33B2, #003A33B2), url(${imageUrl})` }}
        aria-label={`Lire plus sur ${guide.data.preview_title?.[0]?.text || "le guide"}`}
        itemProp="url"
      >
        <div className="line-clamp-6 mb-10">
          {isFilled.richText(guide.data.preview_title) && (
            <PrismicRichText
              field={guide.data.preview_title}
              components={{
                heading2: ({ children }) => <h3 className="title-guide mb-2">{children}</h3>,
                heading3: ({ children }) => <h3 className="title-guide mb-2">{children}</h3>,
              }}
            />
          )}
          {featured && isFilled.richText(guide.data.preview_description) && (
            <div className="hidden md:block">
              <PrismicRichText
                field={guide.data.preview_description}
                components={{
                  paragraph: ({ children }) => <p className="p-xl">{children}</p>,
                }}
              />
            </div>
          )}
        </div>
        {tag && (
          <div>
            <span className="text-tags tag-light-green text-white" itemProp="keywords">{tag}</span>
          </div>
        )}
      </PrismicLink>
    </article>
  );
};

const GuideList = ({ slice, index, context }) => {
  const { title, title_guides, featured_guide, title_newsletter, rgpd } = {
    ...slice.primary,
    title_guides: slice.primary.title_articles,
  };
  const [guides, setGuides] = useState([]);
  const [page, setPage] = useState(1);
  const guidesPerPage = 16;
  const [selectedTag, setSelectedTag] = useState("");
  const listRef = useRef(null);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const fetchGuides = async () => {
      const client = createClient();
      const response = await client.getAllByType("guide");
      const visibles = response.filter(guide => guide.data.visible === true);

      // Exclure le guide à la une
      const filtered = isFilled.contentRelationship(featured_guide)
        ? visibles.filter(guide => guide.id !== featured_guide.id)
        : visibles;

      setGuides(filtered);
    };
    fetchGuides();
  }, [featured_guide]);

  useEffect(() => {
    if (hasMounted && listRef.current) {
      listRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [page]);

  // Récupérer tous les tags uniques
  const allTags = Array.from(new Set(guides.flatMap(guide => guide.tags)));

  // Filtrer les guides selon le tag sélectionné
  const filteredGuides = selectedTag
    ? guides.filter(guide => guide.tags.includes(selectedTag))
    : guides;

  const totalPages = Math.ceil(filteredGuides.length / guidesPerPage);
  const paginatedGuides = filteredGuides.slice(
    (page - 1) * guidesPerPage,
    page * guidesPerPage
  );
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={` ${index == 0 ? 'pb-30' : 'py-16 md:py-23 '} bg-light-gray framing text-dark-green`}
      aria-label="Section guides"
    >
      <div className="max-w-350 mx-auto">
        <div>
          <Breadcrumbs context={context} index={index} textcolor={"text-dark-green"} />
          <div className="flex flex-col gap-20 lg:gap-30">
            <div className={` ${index == 0 ? 'md:pt-5' : 'py-0'}`}>
              <div className="flex flex-col gap-12">
                <div className="max-w-200">
                  {isFilled.richText(title) && (
                    <PrismicRichText
                      field={title}
                      components={{
                        heading1: ({ children }) => (
                          <h1 className="h1" itemProp="name">{children}</h1>
                        ),
                        heading2: ({ children }) => (
                          <h2 className="h1" itemProp="name">{children}</h2>
                        ),
                      }}
                    />
                  )}
                </div>
                <div>
                  {isFilled.contentRelationship(featured_guide) && (
                    <GuideCard guide={featured_guide} featured />
                  )}
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col gap-10 " ref={listRef}>
              {isFilled.richText(title_guides) && (
                <PrismicRichText
                  field={title_guides}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="h3">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="h3">{children}</h3>
                    ),
                  }}
                />
              )}
              <div className="flex flex-col gap-10 lg:gap-20">
                <nav className="w-full flex flex-wrap gap-5" aria-label="Filtrer par tag">
                  <div className="w-full md:w-1/2 lg:hidden">
                    <select
                      value={selectedTag}
                      onChange={e => { setSelectedTag(e.target.value); setPage(1); }}
                      className="w-full"
                      aria-label="Filtrer les guides par tag"
                    >
                      <option value="">Tous les guides</option>
                      {allTags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>
                  <div className="hidden lg:flex flex-wrap gap-5">
                    <div
                      onClick={() => { setSelectedTag(""); setPage(1); }}
                      className={`cursor-pointer tag-filter ${selectedTag === "" ? "bg-dark-green text-white" : "bg-transparent text-dark-green"}`}
                    >
                      Tous les guides
                    </div>
                    {allTags.map(tag => (
                      <div
                        key={tag}
                        onClick={() => { setSelectedTag(tag); setPage(1); }}
                        className={`cursor-pointer tag-filter ${selectedTag === tag ? "bg-dark-green text-white" : "bg-transparent text-dark-green"}`}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                </nav>
                <div className="flex flex-col lg:flex-row gap-10 justify-between">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full">
                    {paginatedGuides.map((guide, i) => (
                      <GuideCard key={guide.id || i} guide={guide} />
                    ))}
                  </div>
                  <aside className="lg:w-130" aria-label="Newsletter">
                    <div className="bg-green text-white p-8">
                      <div className="mb-4">
                        {isFilled.richText(title_newsletter) && (
                          <PrismicRichText
                            field={title_newsletter}
                            components={{
                              heading3: ({ children }) => (
                                <h3 className="h3-sm">{children}</h3>
                              ),
                              heading4: ({ children }) => (
                                <h4 className="h3-sm">{children}</h4>
                              ),
                            }}
                          />
                        )}
                      </div>
                      <form className="flex flex-col md:flex-row flex-wrap gap-2.5 md:gap-4">
                        <input
                          type="email"
                          name="email"
                          placeholder="Ton adresse e-mail"
                          required
                          className="w-full md:w-2/3 lg:w-full"
                          aria-label="Adresse e-mail"
                        />
                        <label className="md:order-3 xl:order-2">
                          <input type="checkbox" name="privacy" required />
                          {isFilled.richText(rgpd) && (
                            <PrismicRichText
                              field={rgpd}
                              components={{
                                paragraph: ({ children }) => (
                                  <span className="p-sm">{children}</span>
                                ),
                              }}
                            />
                          )}
                        </label>
                        <button type="submit" className="cta-text mt-5 px-5 w-full md:mt-0 md:order-2 xl:order-3 md:flex-1 xl:flex-none xl:px-20 xl:mt-5">
                          VALIDER
                        </button>
                      </form>
                    </div>
                  </aside>
                </div>
              </div>
            </div>
            {/* Pagination numérotée */}
            {totalPages > 1 && (
              <nav className="flex gap-2 mt-6 justify-center" aria-label="Pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPage(i + 1)}
                    className={`w-8 rounded-full aspect-square cursor-pointer transition-all text-pagination text-dark-green ${page === i + 1 ? 'bg-yellow' : 'bg-transparent'}`}
                    aria-current={page === i + 1 ? "page" : undefined}
                    aria-label={`Page ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </nav>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuideList;
